import { Component, inject, Injector } from '@angular/core';
import { ExpertsService } from '../_services/experts.service';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ActivatedRoute, Router } from '@angular/router';
import { TopbarService } from 'src/app/core/topbar/topbar.service';
import { RxFormBuilder, RxFormGroup } from '@rxweb/reactive-form-validators';
import { FormValidatorService } from 'src/app/core/services/form-validator.service';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { CountriesService } from '../../common/_services/countries.service';
import { Expert } from '../_types/expert';
import { firstValueFrom } from 'rxjs';
import { IParamsCollection } from 'src/app/core/jsonapi/interfaces/params-collection';
import { AddressesService } from '../../common/_services/addresses.service';
import { Address } from '../../common/_types/address';
import { LoginService } from '../../login/login.service';
import { StringUtilsService } from 'src/app/core/services/string-utils.service';
import { UsersService } from '../../users/_services/users.service';
import { RolesService } from '../../roles/_services/roles.service';
import { LanguagesService } from '../../common/_services/languages.service';
import { UserTypesService } from '../../users/_services/users-types.service';
import { User } from '../../users/_types/user';
import { Filter, FilterOperation } from 'src/app/core/jsonapi/filter';
import { FilterBuilder } from 'src/app/core/jsonapi/filters-builder';

@Component({
    selector: 'app-experts-detail',
    templateUrl: './experts-detail.component.html',
    styleUrls: ['./experts-detail.component.scss'],
    standalone: false
})
export class ExpertsDetailComponent {
  public expertFormGroup!: RxFormGroup;

  public expert: Expert = new Expert();

  public allowWrite: boolean = false;
  public allowDelete: boolean = false;

  public countries: any = [];

  private injector = inject(Injector);
  private dynamicDialogRef?: DynamicDialogRef | null;
  private config?:DynamicDialogConfig | null;
  public visibleChangePass: boolean = false;
  public password: string = "";
  public roles: any[] = [];
  public userTypeMediator: any;

  constructor(
    private expertsService: ExpertsService,
    private addressesService: AddressesService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private route: ActivatedRoute,
    private router: Router,
    private topbarService: TopbarService,
    private formBuilder: RxFormBuilder,
    private formValidationService: FormValidatorService,
    private countriesService: CountriesService,
    private dialogService: DialogService,
    private loginService: LoginService,
        private stringUtilsService: StringUtilsService,
        private usersService: UsersService,
        private languageService: LanguagesService,
        private rolesService: RolesService,
        private userTypeService: UserTypesService
  ) {

    this.config = this.injector.get(DynamicDialogConfig, null);
    this.dynamicDialogRef = this.injector.get(DynamicDialogRef, null);
    this.expert.user = new User();
    this.expertFormGroup = this.formBuilder.formGroup<Expert>(this.expert) as RxFormGroup;
  }

  async ngOnInit() {
    this.topbarService.setTitle("Detalles de perito");
    this.topbarService.addBreadcrumb("Peritos", "/experts");
    this.loadCountries();
    this.loadRoles();

    let id = this.route.snapshot.params['guid'];
    if(this.config?.data.id) id = this.config?.data.id;
    if (id != null && id != '0') {
      await this.loadExpert(id);
    }

    if (this.expert.address == null) this.expert.address = new Address();

    this.allowWrite = this.loginService.hasPermission("EXPERTS.WRITE");
    this.allowDelete = this.loginService.hasPermission("EXPERTS.DELETE");


  }

  async loadExpert(id: string) {
    this.expert = await firstValueFrom(this.expertsService.get(id, { include: ['address'] }));
    //cargamos usuario si tiene
    if (this.expert.userId != null) {
      let user: User = await firstValueFrom(this.usersService.get(this.expert.userId));
      this.expert.user = user;
    }
    this.expertFormGroup.patchValue(this.expert);
    this.expertFormGroup.get('name')?.disable();
  }

  async loadCountries() {
    let params: IParamsCollection = {
      sort: ['name']
    };
    let response = await firstValueFrom(this.countriesService.all(params));
    this.countries = response.data;
  }

  async saveConfigurations(expert: Expert) {
  
    if (!expert.user) expert.user = new User();

    //copiamos datos de operario al usuario vionculado
    if (expert.user != null) {
      expert.user.name = this.expert.name;
      expert.user.email = this.expert.email;
    }

    // si enableAccess es true
    if (expert.enableAccess) {
      // si tiene usuario asignado, se activa el usuario
      if (expert.userId) {
        //activar usuario 
        let user = await firstValueFrom(this.usersService.get(expert.userId));

        user.userName = expert.user.userName;
        user.roleId = expert.user.roleId;
        user.active = true;
        await firstValueFrom(this.usersService.save(user));
      }

      //si no tiene usuario asignado, se crea un usuario y se asigna
      else {
        //ponemos idioma por defecto español
        var languages = await firstValueFrom(this.languageService.all({ filter: FilterBuilder.fromObject({ cultureCode: new Filter(FilterOperation.equals, 'cultureCode', '', 'es-ES') }).build() }));
        if (languages.data.length == 0) {
          this.messageService.add({ closable: true, severity: 'error', detail: "No se ha podido crear el usuario de acceso por no haber encontrado el idioma por defecto Español." });
          return;
        }
        //crear usuario
        let user: User = new User();
        user.isWorker = false;
        user.isExternalUser = true;
        user.userTypeId = expert.user.userTypeId; //aqui será perito.
        user.languageId = languages.data[0].id!;
        user.name = this.expert.name;
        user.roleId = expert.user.roleId;
        user.userName = expert.user.userName;
        //la contraseña inicial NO se guarda en claro: solo viaja su MD5 en Password. Quien da
        //el alta la sigue viendo en el formulario para comunicársela al usuario, pero no queda
        //almacenada en TemporalPassword.
        user.password = this.stringUtilsService.ToMD5(expert.user.password);
        let newUser = await firstValueFrom(this.usersService.save(user));
        expert.userId = newUser.id ? newUser.id : "";
        await firstValueFrom(this.expertsService.save(expert));
      }
    }

    // si enableAccess es false, y tiene usuario asignado, se desactiva el usuario
    if (!expert.enableAccess && expert.userId) {
      let user = await firstValueFrom(this.usersService.get(expert.userId));
      user.active = false;
      await firstValueFrom(this.usersService.save(user));
    }
  }

  async save() {

    if(!this.expertFormGroup.get("enableAccess")?.value){
      this.expertFormGroup.get('user')?.disable();
    }

    let valid = this.formValidationService.validateForm(this.expertFormGroup);
    if (valid || !this.expert.id) {
      this.expert = Object.assign(this.expert, this.expertFormGroup.value);

      if (this.expert.address != null) {
        let address: any = await firstValueFrom(this.addressesService.save(this.expert.address));
        this.expert.addressId = address.id;
      }

      let expertItem = await firstValueFrom(this.expertsService.save(this.expert));
      this.expert.id = expertItem.id;
      this.saveConfigurations(this.expert);

      this.messageService.add({ closable: true, severity: 'success', summary: "Éxito", detail: "Perito guardado correctamente." });

      if(this.dynamicDialogRef){
        this.dynamicDialogRef.close();
      }else{
        if (this.expert.id != "undefined" && this.expert.id != "") {
          this.router.navigate(['experts/' + expertItem.id]);
        }
        this.loadExpert(expertItem.id!);
      }

    } else {
      this.messageService.add({ closable: true, severity: 'error', summary: "Error", detail: "Existen errores, por favor revise el formulario de datos." });
    }
  }

  async delete() {
    this.confirmationService.confirm({
      message: "¿Está seguro que desea eliminar el perito?",
      header: "Eliminar perito",
      acceptLabel: "Aceptar",
      rejectLabel: "Cancelar",
      acceptButtonStyleClass: "p-button-danger",
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.expertsService.delete(this.expert.id).subscribe({
          next: (data: any) => {
            if(this.expert.userId){
              this.usersService.delete(this.expert.userId).subscribe({
                next: (data: any) => {      
                  this.cancel();
                  this.messageService.add({ closable: true, severity: 'success', detail: "Perito eliminado correctamente." });
                      },
                      error: (error: any) => {
                        this.messageService.add({ closable: true, severity: 'error', detail: error.error.title });
                      }
                    });
            }else{
              this.cancel();
              this.messageService.add({ closable: true, severity: 'success', detail: "Perito eliminado correctamente." });
            }
          },
          error: (error: any) => {
            this.messageService.add({ closable: true, severity: 'error', detail: error.error.title });
          }
        });
      }
    });
  }

  cancel() {
    if(this.dynamicDialogRef){
      this.dynamicDialogRef.close();
    }else{
      this.router.navigate(['experts']);
    }
  }

  async loadRoles() {
    let filters = {
      externalAllowed: new Filter(FilterOperation.equals, 'externalAllowed', "", true)
    };
    let params: IParamsCollection = {}
    params.filter = FilterBuilder.fromObject(filters).build();
    let response = await firstValueFrom(this.rolesService.all(params));
    this.roles = response.data;

    let filterUserType = {code: new Filter(FilterOperation.equals, 'code',"", "expert") }
    let params2: IParamsCollection = {}
    params2.filter = FilterBuilder.fromObject(filterUserType).build();
    let response2 = await firstValueFrom(this.userTypeService.all(params2));
    
    this.userTypeMediator = response2.data && response2.data.length > 0 ? response2.data[0] : null;
  }

  async onEnableAccessChange() {
      //si no tiene usuario preparamos datos por si lo activa
      if (this.expertFormGroup.value.enableAccess) {
        
        if (this.expert.userId == null) {
          if (!this.expert.user) this.expert.user = new User();

          this.expert.user.userName = this.stringUtilsService.RandomString(8);
          if (this.expert.user.password == null || this.expert.user.password == "") {
            this.expert.user.temporalPassword = this.stringUtilsService.RandomString(8);
            this.expert.user.password = this.expert.user.temporalPassword;
          }
  
          //si no tiene idioma, lo asignamos por defecto español
          if (this.expert.user.languageId == null) {
            var languages = await firstValueFrom(this.languageService.all({ filter: FilterBuilder.fromObject({ cultureCode: new Filter(FilterOperation.equals, 'cultureCode', '', 'es-ES') }).build() }));
            if (languages.data.length == 0) {
              this.messageService.add({ closable: true, severity: 'error', detail: "No se ha podido activar el usuario de acceso por no haber encontrado el idioma por defecto Español." });
              return;
            }
            this.expert.user.languageId = languages.data[0].id!;
          }
  
          if (!this.expert.user.roleId && this.roles.length == 1) this.expert.user.roleId = this.roles[0].id;
          if(this.userTypeMediator) this.expert.user.userTypeId = this.userTypeMediator.id;
          
        }
        if (this.expert.user) this.expert.user.name = this.expert.name;

        this.expertFormGroup.patchValue({ user: this.expert.user });
  
        this.expertFormGroup.get('user')?.enable();
      } else {
        this.expertFormGroup.get('user')?.disable();
      }
    }

    async changePassword() {
      this.visibleChangePass = true;
    }
    saveChangePassword(changeUserPassword: string) {
      if (changeUserPassword != null && changeUserPassword != "") {
        let user: User = this.expertFormGroup.get('user')?.value;
        user.password = this.stringUtilsService.ToMD5(changeUserPassword);
        this.usersService.save(user).subscribe({
          next: (data: any) => {
            this.messageService.add({ closable: true, severity: 'success', detail: "Contraseña modificada correctamente." });
            this.visibleChangePass = false;
            this.password = "";
          },
          error: (error: any) => {
            console.log(error);
          }
        });
      }
    }
}
